import Base64 from 'crypto-js/enc-base64';
import HmacSHA256 from 'crypto-js/hmac-sha256';
import { IOrderInfo, ISymbol, ITicker, Market, OrderState } from './market-base.class';
import { IMarketSettingsToken } from "./IMarketSettingsToken";

interface ISpotAccountIdData
{
    id: number,
    type: string,
}

interface IResponce
{
     status: string,
}

interface IGetSpotAccountIdResponce extends IResponce
{
    data: ISpotAccountIdData[];
}

interface IGetSymbolesResponce extends IResponce
{
    data: any[];//todo
}

interface IGetBalanceResponce extends IResponce
{
    data: { list: any[]};//todo
}

interface ITickerResponce extends IResponce
{
    tick: {
        ask: any[],
        bid: any[]
    };//todo
}

interface IGetOrderInfoResponce extends IResponce
{
    data: any;//todo
}

export class MarketHuobi extends Market <IMarketSettingsToken>{

  protected name: string = 'Huobi Global';
  protected icon: string = '/assets/images/exchanges/huobi.svg';

  private accessUrl: string = 'https://api.huobi.pro';
  private accessDomain: string = 'api.huobi.pro';
  private signatureMethod = 'HmacSHA256';
  private signatureVersion = '2';

  public async checkConnection(): Promise<boolean> {

    const account = await this.getSpotAccountId();

    return Boolean(account);
  }

  public async getSpotAccountId(): Promise<number> {
    const queryString = this.createQueryString();
    const endpoint = `/v1/account/accounts`;

    const preSignedQuery = this.getPreSignedText('GET', endpoint, queryString);

    const signature = this.getQuerySignature(preSignedQuery);

    const url = `${this.accessUrl}${endpoint}?${queryString}&Signature=${encodeURIComponent(signature)}`;

    try {
      const response = await fetch(url);
      const responseJSON = <IGetSpotAccountIdResponce>await response.json();

      if (responseJSON.status === 'ok') {
        const accounts = responseJSON.data;

        return accounts.find(_account => _account.type === 'spot').id;
      }

      return null;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  public async getCurrencies(): Promise<string[]> {
    const queryString = this.createQueryString();
    const endpoint = `/v1/common/currencys`;

    const preSignedQuery = this.getPreSignedText('GET', endpoint, queryString);

    const signature = this.getQuerySignature(preSignedQuery);

    const url = `${this.accessUrl}${endpoint}?${queryString}&Signature=${encodeURIComponent(signature)}`;

    try {
      const response = await fetch(url);
      const responseJSON = await response.json();

      if (responseJSON['status'] === 'ok') {
        return responseJSON.data;
      }

      return null;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  public async getSymbols(_currency: string = ''): Promise<ISymbol[]> {
    const queryString = this.createQueryString();
    const endpoint = `/v1/common/symbols`;

    const preSignedQuery = this.getPreSignedText('GET', endpoint, queryString);

    const signature = this.getQuerySignature(preSignedQuery);

    const url = `${this.accessUrl}${endpoint}?${queryString}&Signature=${encodeURIComponent(signature)}`;

    try {
      const response = await fetch(url);
      const responseJSON = <IGetSymbolesResponce>await response.json();

      if (responseJSON.status === 'ok') {
        const symbols = responseJSON.data;

        return symbols.filter(_symbol => _symbol.symbol.toLowerCase().includes(_currency.toLowerCase())).map(_symbol => ({
          symbol: _symbol.symbol,
          base_currency: _symbol['base-currency'],
          quote_currency: _symbol['quote-currency'],
          precision: _symbol['amount-precision']
        }));
      }

      return null;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  public async getBalance(_currency: string): Promise<string> {
    const accountId = await this.getSpotAccountId();
    const queryString = this.createQueryString();
    const endpoint = `/v1/account/accounts/${accountId}/balance`;

    const preSignedQuery = this.getPreSignedText('GET', endpoint, queryString);

    const signature = this.getQuerySignature(preSignedQuery);

    const url = `${this.accessUrl}${endpoint}?${queryString}&Signature=${encodeURIComponent(signature)}`;

    try {
      const response = await fetch(url);
      const responseJSON = <IGetBalanceResponce>await response.json();

      if (responseJSON.status === 'ok') {
        const balances = responseJSON.data.list;

        const currencyBalance = balances.find(_balance => _balance.currency.toLowerCase() === _currency.toLowerCase() && _balance.type === 'trade');

        if (Boolean(currencyBalance)) {
          return currencyBalance.balance;
        }

        return '0';
      }

      return null;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  public async getTicker(_symbol: string): Promise<ITicker> {
    const queryString = this.createQueryString({symbol: _symbol.toLowerCase()});
    const endpoint = `/market/detail/merged`;

    const preSignedQuery = this.getPreSignedText('GET', endpoint, queryString);

    const signature = this.getQuerySignature(preSignedQuery);

    const url = `${this.accessUrl}${endpoint}?${queryString}&Signature=${encodeURIComponent(signature)}`;

    try {
      const response = await fetch('/api/market/huobi', {
        method: 'POST',
        headers: {
          'Content-type': 'application/json'
        },
        body: JSON.stringify({
          method: 'GET',
          url: url
        })
      });
      const responseJSON = <ITickerResponce>await response.json();

      if (responseJSON.status === 'ok') {
        const ticker = responseJSON.tick;

        return {
          buy: ticker.ask[0],
          sell: ticker.bid[0]
        };
      }

      return null;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  public async getOrderInfo(_orderId: string): Promise<IOrderInfo> {
    const queryString = this.createQueryString();
    const endpoint = `/v1/order/orders/${_orderId}`;

    const preSignedQuery = this.getPreSignedText('GET', endpoint, queryString);

    const signature = this.getQuerySignature(preSignedQuery);

    const url = `${this.accessUrl}${endpoint}?${queryString}&Signature=${encodeURIComponent(signature)}`;

    try {
      const response = await fetch(url);
      const responseJSON = <IGetOrderInfoResponce>await response.json();

      if (responseJSON.status === 'ok') {
        const orderData = responseJSON.data;

        return this.parseOrderInfo(orderData);
      }

      return null;
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  public async getOrdersHistory(): Promise<IOrderInfo[]> {
    const queryString = this.createQueryString();
    const endpoint = `/v1/order/history`;

    const preSignedQuery = this.getPreSignedText('GET', endpoint, queryString);

    const signature = this.getQuerySignature(preSignedQuery);

    const url = `${this.accessUrl}${endpoint}?${queryString}&Signature=${encodeURIComponent(signature)}`;

    try {
      const response = await fetch(url);
      const responseJSON = await response.json();

      if (responseJSON['status'] === 'ok') {
        const ordersData = responseJSON.data;

        const result = ordersData.filter(_orderData => _orderData.type.includes('market')).map(_orderData => this.parseOrderInfo(_orderData));

        return result;
      }

      return [];
    } catch (error) {
      console.error(error);

      throw error;
    }
  }

  public async createOrder(_symbol: string, _amount: string, _type: 'buy' | 'sell'): Promise<string> {
    const accountId = await this.getSpotAccountId();
    const type = _type === 'buy' ? 'buy-market' : 'sell-market';

    const queryString = this.createQueryString({});
    const endpoint = `/v1/order/orders/place`;

    const preSignedQuery = this.getPreSignedText('POST', endpoint, queryString);

    const signature = this.getQuerySignature(preSignedQuery);

    const url = `${this.accessUrl}${endpoint}?${queryString}&Signature=${encodeURIComponent(signature)}`;

    try {
      const response = await this.http.post(url, {
          'account-id': accountId.toString(),
          'amount': _amount,
          'source': 'api',
          'symbol': _symbol,
          'type': type
        }).toPromise();

      if (response['status'] === 'ok') {
        return response['data'].toString();
      } else if (response['status'] === 'error') {
        throw new Error(this.parseOrderResponse(response));
      }

      return null;
    } catch (error) {
      throw error;
    }
  }
  
  private parseOrderInfo(_orderInfo: any): IOrderInfo {
    let state: OrderState = 'processing';

    if (_orderInfo.state === 'filled') {
      state = 'complete';
    } else if (_orderInfo.state === 'canceled') {
      state = 'canceled';
    }

    let type: 'buy' | 'sell' = 'buy';

    if (_orderInfo.type === 'sell-market') {
      type = 'sell';
    }

    let price = _orderInfo.price;

    if (price === '0.0' && type === 'buy') {
      price = (Number(_orderInfo.amount) / (Number(_orderInfo['field-amount']) + Number(_orderInfo['field-fees']))).toString();
    } else if (price === '0.0' && type === 'sell') {
      price = (Number(_orderInfo['field-cash-amount']) / Number(_orderInfo['amount'])).toString();
    }

    const currencies = this.coinsService.parseExchangeSymbol(_orderInfo['symbol']);

    return {
      orderId: _orderInfo.id,
      sendAmount: _orderInfo.amount,
      getAmount: type === 'sell' ?  _orderInfo['field-cash-amount'] : _orderInfo['field-amount'],
      fee: _orderInfo['field-fees'],
      finishedAt: _orderInfo['finished-at'],
      price: price,
      state: state,
      type: type,
      symbol: _orderInfo['symbol'],
      currencySend: type === 'sell' ? currencies[0] : currencies[1],
      currencyGet: type === 'sell' ? currencies[1] : currencies[0]
    };
  }

  private parseOrderResponse(_response: any) {
    if (_response['err-code'] === 'invalid-amount') return _response['err-code'];

    if (Boolean(_response['err-msg'])) return _response['err-msg'];

    return 'FORM.ERRORS.SMTH-WENT-WRONG';
  }

  private createQueryString(_params: Object = {}) {
    const timestamp = new Date().toISOString().slice(0, -5);

    const defaultParams = {
      AccessKeyId: this.settings.access_key,
      SignatureMethod: this.signatureMethod,
      SignatureVersion: this.signatureVersion,
      Timestamp: timestamp
    };

    const params = {...defaultParams, ..._params};

    let result = '';

    for (let key in params) {
      result += `&${key}=${encodeURIComponent(params[key])}`;
    }

    return result.slice(1);
  }

  private getPreSignedText(_method: string, _endpoint: string, _queryParams: string) {
    return `${_method}\n${this.accessDomain}\n${_endpoint}\n${_queryParams}`;
  }

  private getQuerySignature(_query: string) {
    const hash = HmacSHA256(_query, this.settings.secret_key);
    const base64Hash = Base64.stringify(hash);

    return base64Hash;
  }

}
